import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SocketKey } from '@/Config/api/socketKey';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { useSocket } from '@/Domain/components/SocketProvider';
import { ConversationAssigneeEnum } from '@/Domain/enum/ConversationAssignee.enum';
import { useAudioNotification } from '@/Domain/hooks/Audio/useAudioNotification.hook';
import { useUser } from '@/Domain/hooks/User/useUser.hook';

type Props = {
  // An anchored list is two queries, so this refreshes whatever the Inbox currently renders.
  refetch: () => Promise<unknown>;
  query: string;
};

type ConversationUpdatedEvent = {
  conversation: ConversationDto;
};

export const useConversationSocket = ({ refetch, query }: Props) => {
  const { socket } = useSocket();
  const { playNotification } = useAudioNotification();
  const { user } = useUser();
  const location = useLocation();

  const queryRef = useRef(query);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  useEffect(() => {
    socket.on(SocketKey.conversation.conversationCreated, () => {
      const shouldRefetch = queryRef.current.trim() === '';
      if (shouldRefetch) refetch().catch(() => null);
      playNotification('newConversation', 'socket');
    });

    socket.on(SocketKey.conversation.conversationUpdated, (data: ConversationUpdatedEvent) => {
      const shouldRefetch = queryRef.current.trim() === '';
      if (shouldRefetch) refetch().catch(() => null);

      // Check if the conversation has unread messages to avoid double notifications
      const hasUnreadMessages = data.conversation?.has_unread_messages;
      const assignee = data.conversation?.assignee;

      // Check if the conversation is assigned to the current user
      const isAssignedToMe =
        assignee?.type === ConversationAssigneeEnum.HUMAN_OPERATOR &&
        assignee?.user?.id === user?.id;

      // Check if the conversation is unassigned
      const isHandoff = assignee?.type === ConversationAssigneeEnum.UNASSIGNED;

      // Play appropriate notification based on assignment
      if (hasUnreadMessages) {
        if (isAssignedToMe) {
          playNotification('newMessageToMe', 'socket');
        } else if (isHandoff) {
          playNotification('handoff', 'socket');
        } else {
          playNotification('newMessage', 'socket');
        }
      }
    });
    return () => {
      socket.off(SocketKey.conversation.conversationCreated);
      socket.off(SocketKey.conversation.conversationUpdated);
    };
  }, [socket, playNotification, user?.id, location.pathname]);
};
